import React from 'react';
import { PageHeader } from '@/components/ui/page-header';
import { Container } from '@/components/ui/container';

export function TermsPage() {
  return (
    <div className="min-h-screen bg-background">
      <Container className="py-8 max-w-4xl">
        <PageHeader 
          heading="Terms & Conditions" 
          subheading="Sleepnest.app Terms and Conditions of Use"
        />
        
        <main className="mt-8 prose prose-slate max-w-none dark:prose-invert">
          <section className="mb-8">
            <p className="text-muted-foreground">
              Thank you for visiting Sleepnest.app (the "Site"). The Site is an Internet property of 
              GentleBirth PR LLC ("GentleBirth™," "we" or "us"). You agree to the following GentleBirth™ 
              Terms and Conditions (the "Terms and Conditions"), in their entirety, when you: (a) access 
              or use the Site; (b) register as a GentleBirth™ member ("Member") at the Site or App, which 
              provides you with access to some or all of the GentleBirth's activities, content, products, 
              services, surveys and tests (collectively, the "GentleBirth™ Services), as well as various 
              interactive features and services (collectively, the "Interactive Services" and together with 
              the GentleBirth™ Services, the "Services"); and/or (c) register for one of our sweepstakes, 
              promotional offers or contests, as offered from time to time (the "Promotions").
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Important Notice</h2>
            <p className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-amber-800 dark:text-amber-200">
              IF YOU ARE A PARENT OR GUARDIAN AND YOU PROVIDE CONSENT FOR YOUR CHILD TO REGISTER 
              WITH THE WEBSITE, OR IF YOU ARE AN EDUCATOR (AS DEFINED BELOW) WHO IS ACTING AS THE 
              AGENT OF A PARENT OR LEGAL GUARDIAN, YOU AGREE TO BE BOUND BY THESE TERMS AND CONDITIONS 
              IN RESPECT OF SUCH CHILD'S USE OF THE WEBSITE.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Medical Disclaimer</h2>
            <p className="text-muted-foreground">
              The Content appearing on the Site is provided for informational purposes only. Much of the 
              Content, as well as the Member Content (as defined below), appearing on the site is provided 
              by GentleBirth and its members. GentleBirth™ shall not be responsible or liable for the accuracy, 
              usefulness or availability of any Content, Member Content or other information transmitted or 
              made available by and through the Site and/or Services. GentleBirth™ is not a medical or health 
              services organization provider or forum and we do not engage in patient diagnosis or the practice 
              of medicine. The Content and Member Content presented by and through the Site and/or Services is 
              in no way intended as medical and/or psychiatric advice or therapy, is not provided for the purpose 
              of diagnosing or treating medical and/or psychiatric problems, nor is it a substitute for medical 
              and/or psychiatric treatment and/or therapy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Age Requirements</h2>
            <p className="text-muted-foreground">
              The GentleBirth™ Offerings are not available to: (A) any users previously suspended or removed 
              from the Site or Services OR (B) ANY PERSONS UNDER THE AGE OF 13.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Registration and Account Information</h2>
            <p className="text-muted-foreground mb-4">
              GentleBirth™ may invite visitors to register on the Site or App to become a GentleBirth subscriber. 
              Visitors may register for membership by and through their Facebook™ account, Twitter™ account or 
              any other means designated by GentleBirth™. GentleBirth™ reserves the right, in its sole discretion, 
              to deny membership, as well as any GentleBirth™ Offering, to anyone, for any reason whatsoever, as 
              permitted by applicable law.
            </p>
            <p className="text-muted-foreground">
              You agree to provide true, accurate, current and complete Registration Data and to update your 
              Registration Data as necessary to maintain it in an up to date and accurate fashion. You are 
              responsible for maintaining the confidentiality of your Member account, user name and password 
              and for restricting access to your computer.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Payment Policy</h2>
            <p className="text-muted-foreground mb-4">
              Access to some sections of the GentleBirth App is free, but we charge for some of our products 
              and services on a subscription fee basis. This Payment Policy applies to users who have purchased 
              a GentleBirth subscription plan to access GentleBirth either online or through a mobile application.
            </p>
            
            <h3 className="text-lg font-semibold mb-2">Subscription Plans and Payments</h3>
            <p className="text-muted-foreground mb-4">
              We offer several types of individual plans and gift plans. The pricing terms applicable to your 
              account may vary depending on promotions, the type of subscription and the term you select at the 
              time of purchase. Most of our subscription plans renew automatically.
            </p>

            <h3 className="text-lg font-semibold mb-2">Refund Policy</h3>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Purchases of subscriptions made through the Apple iTunes Store or our iPhone or iPad applications are final, and are not eligible for a refund.</li>
              <li>Purchases of subscriptions made through the Google Play Store or our Android applications are final, and are not eligible for a refund.</li>
              <li>GentleBirth Workshop are non-refundable.</li>
              <li>GentleBirth Institute enrollments are non-refundable.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">User Content and Restrictions</h2>
            <p className="text-muted-foreground mb-4">
              As a Member, you agree that you are solely responsible for the Member Content and/or other 
              information that you publish, transmit and/or post by and through the Site and/or Services. 
              You agree to use the Site and/or Services in a manner consistent with any and all applicable 
              laws, rules and regulations.
            </p>
            
            <p className="text-muted-foreground">
              In connection with your use of the Site and/or Services, you agree not to engage in activities 
              including but not limited to: impersonating any person or entity, stalking or harassing any person, 
              engaging in unauthorized advertising, transmitting spam, expressing statements endorsed by GentleBirth™ 
              without consent, or interfering with the Services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Intellectual Property Rights</h2>
            <p className="text-muted-foreground">
              All third-party Member Content and other material posted or made available by and through the 
              GentleBirth™ Offerings including, but not limited to, the design, selection, arrangement and 
              coordination of such GentleBirth™ Offerings, is owned by, or licensed to, GentleBirth™ and is 
              protected under applicable copyright, trademark and other proprietary rights. The "GentleBirth™" 
              name and logo are trademarks of GentleBirth™.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Disclaimer of Warranties</h2>
            <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-800 dark:text-red-200 font-semibold mb-2">IMPORTANT:</p>
              <p className="text-red-800 dark:text-red-200">
                THE GENTLEBIRTH™ OFFERINGS, MEMBER CONTENT AND/OR ANY OTHER GENTLEBIRTH™ PRODUCTS 
                AND/OR SERVICES, ARE PROVIDED TO YOU ON AN "AS IS" AND "AS AVAILABLE" BASIS. ANY AND 
                ALL WARRANTIES, EXPRESS AND/OR IMPLIED, ARE DISCLAIMED TO THE FULLEST EXTENT PERMISSIBLE 
                PURSUANT TO APPLICABLE LAW.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Limitation of Liability</h2>
            <p className="text-muted-foreground">
              YOU EXPRESSLY UNDERSTAND AND AGREE THAT GENTLEBIRTH™ SHALL NOT BE LIABLE TO YOU OR ANY 
              THIRD-PARTY FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL AND/OR EXEMPLARY 
              DAMAGES INCLUDING, BUT NOT LIMITED TO, DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA OR 
              OTHER INTANGIBLE LOSSES, TO THE FULLEST EXTENT PERMISSIBLE BY LAW.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Dispute Resolution</h2>
            <p className="text-muted-foreground mb-4">
              The Agreement shall be treated as though it were executed and performed in Mountain View, 
              California, U.S.A. and shall be governed by and construed in accordance with the laws of 
              the State of California, U.S.A.
            </p>
            <p className="text-muted-foreground">
              Should a dispute arise concerning the GentleBirth™ Offerings, the terms and conditions of 
              the Agreement or the breach of same by any party hereto the parties agree to submit their 
              dispute for resolution by binding arbitration before the American Arbitration Association.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Contact Information</h2>
            <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4">
              <p className="text-muted-foreground mb-2">
                To contact us directly with any questions or comments that you may have about these Terms 
                and Conditions, send an email to:
              </p>
              <p className="font-semibold">team@gentlebirth.com</p>
            </div>
          </section>

          <section className="text-sm text-muted-foreground border-t pt-4">
            <p>Last updated: {new Date().toLocaleDateString()}</p>
          </section>
        </main>
      </Container>
    </div>
  );
}